import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../components/Theme";
import GetOperationById from "../../api/operation/GetOperationById";
import CreateHarvestOperation from "../../api/operation/CreateHarvestOperation";
import DeleteOperationById from "../../api/operation/DeleteOperationById";

export default function Harvest({ appCropId }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [harvestData, setHarvestData] = useState([]);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    const fetchData = async () => {
      if (appCropId) {
        try {
          const res = await GetOperationById({ appCropId, appOperId: "OE" });
          setHarvestData(res);
        } catch (err) {
          console.error("Error fetching harvest data:", err);
        }
      }
    };
    fetchData();
  }, [appCropId]);

  const handleChange = (index, key, value) => {
    const updated = [...harvestData];
    updated[index][key] = value;
    setHarvestData(updated);
  };

  const handleAdd = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    setHarvestData([
      ...harvestData,
      {
        appCropId,
        appOperId: "OE" + timestamp,
        operDate: "",
        noOfWorkers: 0,
        workerCost: 0,
        petrolCost: 0,
        equipmentCost: 0,
        fertilizerCost: 0,
        chemicalCost: 0,
        lotNumber: "",
        pctHarvest: 0,
        yieldKg: 0,
        yieldBaht: 0,
        yieldBahtKg: 0,
      },
    ]);
  };

  const openDeleteDialog = (id) => {
    setSelectedDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await DeleteOperationById({ appOperId: selectedDeleteId });
      setHarvestData((prev) =>
        prev.filter((item) => item.appOperId !== selectedDeleteId)
      );
      setSnackbarSeverity("success");
      setSnackbarMessage("ลบรายการสำเร็จ");
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาดในการลบ");
    } finally {
      setDeleteDialogOpen(false);
      setOpenSnackbar(true);
    }
  };

  const handleSave = async () => {
    const results = await CreateHarvestOperation(harvestData);
    const hasError = results.some((r) => r.status !== 200);
    setSnackbarSeverity(hasError ? "error" : "success");
    setSnackbarMessage(
      hasError ? "บางรายการบันทึกไม่สำเร็จ" : "บันทึกสำเร็จทั้งหมด"
    );
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box p={2}>
      <Box
        width="100%"
        sx={{ backgroundColor: colors.greenAccent[300] }}
        display="flex"
        justifyContent="center"
      >
        <Typography
          p={isMobile ? 1 : 2}
          variant={isMobile ? "body" : "h6"}
          fontWeight="bold"
          color="#fff"
        >
          ตารางการเก็บเกี่ยวผลผลิต
        </Typography>
      </Box>

      <Box maxWidth={isMobile ? "320px" : "none"}>
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                {[
                  "#",
                  "วันที่",
                  "จำนวนคนงาน",
                  "ค่าแรง",
                  "ค่าน้ำมัน",
                  "ค่าอุปกรณ์",
                  "ค่าปุ๋ย",
                  "ค่าสารเคมี",
                  "Lot",
                  "% เก็บเกี่ยว",
                  "ผลผลิต (กก.)",
                  "ผลผลิต (บาท)",
                  "บาท/กก.",
                  "ลบ",
                ].map((text, idx) => (
                  <TableCell key={idx} align={idx === 13 ? "center" : "left"}>
                    {text}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {harvestData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  {[
                    { key: "operDate", type: "date" },
                    { key: "noOfWorkers", type: "number" },
                    { key: "workerCost", type: "number" },
                    { key: "petrolCost", type: "number" },
                    { key: "equipmentCost", type: "number" },
                    { key: "fertilizerCost", type: "number" },
                    { key: "chemicalCost", type: "number" },
                    { key: "lotNumber", type: "text" },
                    { key: "pctHarvest", type: "number" },
                    { key: "yieldKg", type: "number" },
                    { key: "yieldBaht", type: "number" },
                    { key: "yieldBahtKg", type: "number" },
                  ].map((field, i) => (
                    <TableCell key={i}>
                      <TextField
                        type={field.type}
                        size="small"
                        value={row[field.key]}
                        onChange={(e) =>
                          handleChange(index, field.key, e.target.value)
                        }
                      />
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => openDeleteDialog(row.appOperId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={14} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    variant="outlined"
                  >
                    เพิ่มแถว
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box textAlign="center" mt={3}>
        <Button
          variant="contained"
          sx={{ backgroundColor: colors.greenAccent[400] }}
          onClick={handleSave}
        >
          บันทึกข้อมูล
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            justifyContent: "center",
            minWidth: "200px",
            textAlign: "center",
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>คุณต้องการลบรายการนี้ใช่หรือไม่?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>ยกเลิก</Button>
          <Button color="error" onClick={confirmDelete}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
